import { removeCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import signIn from "@insite/client-framework/Store/Context/Handlers/SignIn";
import {
    getCurrentPage,
    getLocation,
    removeAbsoluteUrl,
} from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getHomePageUrl } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SignInChangePasswordForm from "@insite/content-library/Widgets/SignIn/SignInChangePasswordForm";
import SignInResetPasswordForm from "@insite/content-library/Widgets/SignIn/SignInResetPasswordForm";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupComponentProps } from "@insite/mobius/CheckboxGroup";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconProps } from "@insite/mobius/Icon/Icon";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ReCaptcha from "@tSurya/Overrides/Components/ReCaptcha";
import { isGlobalViewsWebsite, isMgbwWebsite } from "@tSurya/Common/Utilities/websiteUtils";
import { isCrossBrandAuthenticated } from "@tSurya/Common/Utilities/brandPermissionUtils";
import BrandAccessDeniedModal from "@tSurya/Components/BrandAccessDeniedModal";
import { COLORS, ELEMENTS, TYPOGRAPHY } from "@tSurya/theme/design";
import { isThemeActive, THEME_IDS } from "@tSurya/theme/themeHelper";
import React, { RefObject,useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    changePasswordInstructions = "changePasswordInstructions",
    fullWidthButton = "fullWidthButton",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.changePasswordInstructions]: string;
        [fields.fullWidthButton]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const parsedQuery = parseQueryString<{ returnUrl?: string; externalError?: string }>(location.search);
    const pageType = getCurrentPage(state).type;
    const isSignInPage = pageType === "SignInPage";
    const isConfirmationPage = pageType === "OrderConfirmationPage" || pageType === "OnePageCheckoutConfirmationPage";
    const returnUrl = isSignInPage
        ? removeAbsoluteUrl(parsedQuery.returnUrl)
        : `${location.pathname}${location.search}`;
    return {
        accountSettings: getSettingsCollection(state).accountSettings,
        returnUrl,
        externalError: parsedQuery.externalError,
        isConfirmationPage,
        homePageUrl: getHomePageUrl(state),
        sessionProperties: state.context.session.properties,
    };
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme>;

const mapDispatchToProps = {
    signIn,
    validateReCaptcha: makeHandlerChainAwaitable<{ location: string; recaptchaRef: RefObject<ReCAPTCHA | null> }, boolean>(
        validateReCaptcha,
    ),

};

export interface SignInExistingAccountStyles {
        wrapper?: InjectableCss;
    form?: InjectableCss;
    rememberMeCheckbox?: CheckboxProps;
    rememberMeCheckboxGroup?: CheckboxGroupComponentProps;
    userNameTextField?: TextFieldProps;
    signInGridContainer?: GridContainerProps;
    icon?: IconProps;
    passwordTextField?: TextFieldProps;
    signInButton?: ButtonPresentationProps;
    signInButtonGridItem?: GridItemProps;
    forgotPasswordGridItem?: GridItemProps;
    rememberMeGridItem?: GridItemProps;
    passwordGridItem?: GridItemProps;
    userNameGridItem?: GridItemProps;
    signInExistingAccountTitle?: TypographyProps;
    forgotPasswordLink?: LinkPresentationProps;
    resetPasswordModal?: ModalPresentationProps;
    changePasswordModal?: ModalPresentationProps;
    reCaptchaGridItem?: GridItemProps;
}

export const signInExistingAccountStyles: SignInExistingAccountStyles = {
    wrapper: {
        css: css`
            background-color: #ffffff;
            overflow: hidden;
            border: 1px solid ${getColor("common.border")};
        `,
    },
    signInGridContainer: {
        css: css`
            padding: 35px 33px 40px 32px;
        `,
    },
    reCaptchaGridItem: {
        width: 12,
    },
    icon: {
        css: css`
            padding-top: 50px;
        `,
    },
    signInExistingAccountTitle: {
        variant: "h4",
        css: css`
            padding: 20px 30px;
            margin-bottom: 0;
            font-family: ${TYPOGRAPHY.headings.fontFamily};
            font-style: ${TYPOGRAPHY.headings.fontStyle};
            font-size: ${TYPOGRAPHY.cardSectionTitle.size};
            line-height: ${TYPOGRAPHY.lineHeight.medium};
            color: ${getColor("primary")};
            background-color: ${getColor("common.backgroundContrast")}; ;
        `,
    },
    userNameGridItem: {
        width: 12,
        css: css`
            font-weight: 800 !important;
            padding: 15px 15px 0;
            > div[class^="FormFieldStyle"] {
                > div[class^="FormInputWrapper"] {
                    margin-top: 4px !important;
                    > #userName {
                        font-family: ${TYPOGRAPHY.fontFamily.title};
                        font-style: normal;
                        font-weight: ${TYPOGRAPHY.weight.base};
                        font-size: ${TYPOGRAPHY.size.small};
                        line-height: ${TYPOGRAPHY.lineHeight.xbase};
                        /* color: ${getColor("text.accent")}; */
                        color: ${getColor("text.main")};
                        background: ${getColor("common.backgroundContrast")} !important;
                        &::placeholder {
                            background: ${getColor("common.backgroundContrast")} !important;
                            font-family: ${TYPOGRAPHY.fontFamily.title};
                            font-style: normal;
                            font-weight: ${TYPOGRAPHY.weight.base};
                            font-size: ${TYPOGRAPHY.size.small};
                            line-height: ${TYPOGRAPHY.lineHeight.xbase};
                            /* color: ${getColor("text.accent")}; */
                            color: ${getColor("text.main")};
                        }
                        &:focus {
                            outline: none !important;
                            border: 1px solid ${getColor("text.main")};
                        }
                    }
                }
            }
        `,
    },
    userNameTextField: {
        labelProps: {
            css: css`
                font-family: ${TYPOGRAPHY.fontFamily.title};
                font-style: normal;
                font-weight: ${(props) => isMgbwWebsite() ? TYPOGRAPHY.weight.baseBold : TYPOGRAPHY.weight.bold};
                font-size: ${TYPOGRAPHY.size.smaller};
                line-height: ${TYPOGRAPHY.lineHeight.base};
                color: ${getColor("text.main")} !important;
            `,
        },
        cssOverrides: {
            inputSelect: css`
                border: 1px solid ${getColor("common.border")};
                border-radius: unset;
                background: #ffffff !important;
                &:focus {
                    border-radius: 4px;
                    background: #ffffff !important;
                }
            `,
            formInputWrapper: css`
                input {
                    padding: 0px 2rem 0 10px !important;
                }
                &:focus {
                    padding: 0px 2rem 0 10px !important;
                }
            `,
        },
    },
    passwordTextField: {
        labelProps: {
            css: css`
                font-family: ${TYPOGRAPHY.fontFamily.title};
                font-style: normal;
                font-weight: ${(props) => isMgbwWebsite() ? TYPOGRAPHY.weight.baseBold : TYPOGRAPHY.weight.bold};
                font-size: ${TYPOGRAPHY.size.smaller};
                line-height: ${TYPOGRAPHY.lineHeight.base};
                color: ${getColor("text.main")} !important;
            `,
        },
        cssOverrides: {
            inputSelect: css`
                border: 1px solid ${getColor("common.border")};
                border-radius: unset;
                background: #ffffff !important;
                &:focus {
                    border-radius: 4px;
                }
            `,
            formInputWrapper: css`
                input {
                    padding: 0px 2rem 0 10px !important;
                }
                &:focus {
                    padding: 0px 2rem 0 10px !important;
                }
            `,
        },
    },
    passwordGridItem: {
        width: 12,
        css: css`
            padding: 14px 15px 0;
            > div[class^="FormFieldStyle"] {
                > div[class^="FormInputWrapper"] {
                    margin-top: 4px !important;

                    > #password {
                        font-family: ${TYPOGRAPHY.fontFamily.title};
                        font-style: normal;
                        font-weight: ${TYPOGRAPHY.weight.base};
                        font-size: ${TYPOGRAPHY.size.small};
                        line-height: ${TYPOGRAPHY.lineHeight.xbase};
                        /* color: ${getColor("text.accent")}; */
                        color: ${getColor("text.main")};
                        background: ${getColor("common.backgroundContrast")} !important;
                        &::placeholder {
                            background: ${getColor("common.backgroundContrast")} !important;
                            font-family: ${TYPOGRAPHY.fontFamily.title};
                            font-style: normal;
                            font-weight: ${TYPOGRAPHY.weight.base};
                            font-size: ${TYPOGRAPHY.size.small};
                            line-height: ${TYPOGRAPHY.lineHeight.xbase};
                            /* color: ${getColor("text.accent")}; */
                            color: ${getColor("text.main")};
                        }
                        &:focus {
                            outline: none !important;
                            border: 1px solid ${getColor("text.main")};
                        }
                    }
                }
            }
        `,
    },
    signInButtonGridItem: {
        width: 12,
        css: css`
            justify-content: flex-start;
            padding-top: 36px;
        `,
    },
    rememberMeGridItem: {
        width: 6,
        css: css`
            padding: 8px 15px 0;
            align-items: center;
            > div[class^="CheckboxGroupStyle"] {
                flex-direction: row;
            }
            ${({ theme }: GridItemProps & ThemeProps<BaseTheme>) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-right: 0;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    rememberMeCheckbox: {
        typographyProps: {
            size: 15,
            css: css`
                font-family: ${TYPOGRAPHY.fontFamily.title};
                font-style: normal;
                font-weight: ${TYPOGRAPHY.weight.base};
                font-size: ${TYPOGRAPHY.size.smaller};
                line-height: ${TYPOGRAPHY.lineHeight.base};
                /* color: ${getColor("text.accent")}; */
                color: ${getColor("text.main")};
            `,
        },
    },
    forgotPasswordGridItem: {
        width: 6,
        css: css`
            align-items: center;
            justify-content: flex-end;
            padding: 8px 15px 0;
            ${({ theme }: GridItemProps & ThemeProps<BaseTheme>) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-left: 0;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    forgotPasswordLink: {
        typographyProps: {
            css: css`
                font-family: ${TYPOGRAPHY.fontFamily.title};
                font-style: normal;
                font-weight: ${TYPOGRAPHY.button.weight};
                font-size: ${TYPOGRAPHY.size.base};
                line-height: ${TYPOGRAPHY.lineHeight.xbase};
                color: ${(props) => isMgbwWebsite() ? getColor("text.link")(props) : getColor("text.main")(props)} !important;
            `,
        },
    },

    resetPasswordModal: {
        sizeVariant: "small",
        cssOverrides: {
            modalBody: css`
                border-radius: 0px;
            `,
            modalTitle: css`
                color: ${getColor("primary")};
            `,
            headlineTypography: css`
                font-family: ${TYPOGRAPHY.fontFamily.title} !important;
                font-style: normal;
                font-weight: ${TYPOGRAPHY.weight.bold} !important;
                font-size: ${TYPOGRAPHY.size.large} !important;
                line-height: ${TYPOGRAPHY.lineHeight.medium} !important;
                color: ${getColor("text.main")} !important;
                margin-bottom: 0;
            `,
            titleButton: css`
                padding: 0px !important;
            `,
        },
    },
    changePasswordModal: {
        sizeVariant: "small",
    },
};

/**
 * @deprecated Use signInExistingAccountStyles instead.
 */
export const signInExistingAccount = signInExistingAccountStyles;
const styles = signInExistingAccountStyles;
const StyledForm = getStyledWrapper("form");
const recaptchaRef: RefObject<ReCAPTCHA | null> = React.createRef();

const SignInExistingAccount = ({
    signIn,
    fields,
    accountSettings,
    returnUrl,
    externalError,
    theme,
    isConfirmationPage,
    homePageUrl,
    validateReCaptcha,
    sessionProperties
}: Props) => {
    const [rememberMe, setRememberMe] = useState(false);
    const [keepMeSignedIn, setKeepMeSignedIn] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [userName, setUserName] = useState("");
    const [password, setPassword] = useState("");
    const [isSignInClicked, setIsSignInClicked] = useState(false);
    const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
    const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState(externalError || "");
    const [showBrandDeniedModal, setShowBrandDeniedModal] = useState(false);

    const showRememberMe = accountSettings.rememberMe;
    const showKeepMeSignedIn = accountSettings.keepMeSignedIn;

    const signInHandler = async(newPassword?: string) => {
        removeCookie(".AspNet.ApplicationCookie");
        removeCookie("surya_auth");
        
        const pass = newPassword || password;
        setIsSignInClicked(true);
        if (!userName || !pass) {
            return;
        }

        if (accountSettings.useEmailAsUserName && !validateEmail(userName)) {
            return;
        }
        const isReCaptchaValid = await validateReCaptcha({
            location: "signIn",
            recaptchaRef,
        });

        if (!isReCaptchaValid) {
            return;
        }
        setErrorMessage("");
        if (!isGlobalViewsWebsite()) {
            localStorage.setItem("showPopup", "true");
        }

        signIn({
            userName,
            password: pass,
            rememberMe,
            keepMeSignedIn,
            returnUrl: theme.signIn?.useOverlay && isConfirmationPage ? homePageUrl : returnUrl,
            skipRedirect: theme.signIn?.useOverlay && !isConfirmationPage,
            onError: (error, statusCode) => {
                if (statusCode === 422) {
                    setIsChangePasswordModalOpen(true);
                    return;
                }
                // Show modal only for cross-brand authenticated users (Scenarios 1-3)
                // For guest users (Scenario 4), show error message instead
                if (statusCode === 400 && error === siteMessage("SignInInfo_UserNotAllowedForWebsite") && isCrossBrandAuthenticated(sessionProperties)) {
                    setShowBrandDeniedModal(true);
                    return;
                }
                setErrorMessage(error);
            },
        });
    };

    const submitHandler = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        signInHandler();
    };

    const onResetPasswordClose = () => {
        setIsResetPasswordModalOpen(false);
    };

    const onChangePasswordClose = () => {
        setIsChangePasswordModalOpen(false);
    };

    const onPasswordChanged = (newPassword: string) => {
        setPassword(newPassword);
        setIsChangePasswordModalOpen(false);
        signInHandler(newPassword);
    };

    const validateUserName = () => {
        if (isSignInClicked) {
            if (userName) {
                if (accountSettings.useEmailAsUserName && !validateEmail(userName)) {
                    return siteMessage("SignInInfo_Email_Validation");
                }
            } else {
                return accountSettings.useEmailAsUserName
                    ? siteMessage("SignInInfo_Email_Required")
                    : siteMessage("SignInInfo_UserName_Required");
            }
        }

        return "";
    };

    const userErrorMessage = validateUserName();
    const passwordErrorMessage =
        !password && isSignInClicked ? siteMessage("SignInInfo_Password_Required") : errorMessage || "";

    const signInButtonCss = css`
        padding: 0 34px;

        ${() => {
            // MGBW uses theme-level button.base.hover tokens from Start.tsx
            // Surya/GV need custom hover here because they don't define those tokens
            if (isMgbwWebsite()) {
                return ''; // Let Start.tsx theme handle it
            }
            return css`
                color: ${getColor("text.main")};
                border-radius: ${ELEMENTS.button.base.borderRadius};

                &:hover {
                    background: ${getColor("text.accent")};
                    span[class^="TypographyStyle"] {
                        color: ${getColor("secondary")};
                    }
                }

                > span {
                    font-family: ${TYPOGRAPHY.button.fontFamily};
                    font-style: normal;
                    font-weight: ${TYPOGRAPHY.button.weight};
                    font-size: ${TYPOGRAPHY.button.size};
                    line-height: ${TYPOGRAPHY.button.lineHeight};
                    color: ${getColor("primary.contrast")};
                }
            `;
        }}

        ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                css`
                    width: 100%;
                `,
                css`
                    width: 100%;
                `,
                fields.fullWidthButton
                    ? css`
                          width: 100%;
                      `
                    : null,
                fields.fullWidthButton
                    ? css`
                          width: 100%;
                      `
                    : null,
                fields.fullWidthButton
                    ? css`
                          width: 100%;
                      `
                    : null,
            ])}
    `;

    return (
        <StyledWrapper {...styles.wrapper}>
            <BrandAccessDeniedModal isOpen={showBrandDeniedModal} onClose={() => setShowBrandDeniedModal(false)} />
            <Typography {...styles.signInExistingAccountTitle}>{translate("Already have an account?")}</Typography>
            <StyledForm {...styles.form} onSubmit={submitHandler} noValidate>
                <GridContainer {...styles.signInGridContainer}>
                    <GridItem {...styles.userNameGridItem}>
                        <TextField
                            id="userName"
                            {...styles.userNameTextField}
                            label={accountSettings.useEmailAsUserName ? translate("Email") : translate("User Name")}
                            onChange={e => setUserName(e.currentTarget.value)}
                            value={userName}
                            error={userErrorMessage}
                            autoComplete="username"
                            data-test-selector="signIn_userName"
                            placeholder="Enter User Name"
                        />
                    </GridItem>
                    <GridItem {...styles.passwordGridItem}>
                        <TextField
                            id="password"
                            {...styles.passwordTextField}
                            label={translate("Password")}
                            onChange={e => setPassword(e.currentTarget.value)}
                            value={password}
                            error={passwordErrorMessage}
                            placeholder="Enter your Password"
                            iconProps={{
                                ...styles.icon,
                                src: showPassword ? EyeOff : Eye,
                            }}
                            iconClickableProps={{
                                onClick: () => {
                                    setShowPassword(!showPassword);
                                },
                                type: "button",
                            }}
                            clickableText={password}
                            autoComplete="current-password"
                            data-test-selector="signIn_password"
                            type={showPassword ? "text" : "password"}
                        />
                    </GridItem>
                    <GridItem {...styles.reCaptchaGridItem}>
                        <ReCaptcha recaptchaRef={recaptchaRef} location="signIn" />
                    </GridItem>
                    <GridItem {...styles.rememberMeGridItem}>
                        {showRememberMe && (
                            <CheckboxGroup {...styles.rememberMeCheckboxGroup}>
                                <Checkbox
                                    uid="rememberMe"
                                    {...styles.rememberMeCheckbox}
                                    checked={rememberMe}
                                    onChange={(_, value) => setRememberMe(value)}                                >
                                    {translate("Remember Me")}
                                </Checkbox>
                            </CheckboxGroup>
                        )}
                         {showKeepMeSignedIn && (
                            <CheckboxGroup {...styles.rememberMeCheckboxGroup}>
                                <Checkbox
                                    uid="keepMeSignedIn"
                                    {...styles.rememberMeCheckbox}
                                    checked={keepMeSignedIn}
                                    onChange={(_, value) => setKeepMeSignedIn(value)}
                                >
                                    {translate("Keep Me Signed In")}
                                </Checkbox>
                            </CheckboxGroup>
                        )}
                    </GridItem>                    
                    <GridItem {...styles.forgotPasswordGridItem}>
                        <Link
                            {...styles.forgotPasswordLink}
                            type="button"
                            onClick={() => setIsResetPasswordModalOpen(true)}
                            data-test-selector="signIn_forgotPassword"
                        >
                            {translate("Forgot password?")}
                        </Link>
                        <Modal
                            headline={translate("Reset Password")}
                            {...styles.resetPasswordModal}
                            cssOverrides={{
                                headlineTypography: css`
                                    font-family: ${TYPOGRAPHY.headings.fontFamily} !important;
                                    font-weight: ${TYPOGRAPHY.headings.weight};
                                    font-size: ${isThemeActive(THEME_IDS.MGBW) ? "28px" : "24px"};
                                    margin-bottom: 0;
                                `,
                                modalBody: css`
                                    border-radius: 0px;
                                    button {
                                        span {
                                            font-family: ${TYPOGRAPHY.button.fontFamily} !important;
                                            font-weight: ${TYPOGRAPHY.button.weight} !important;
                                            font-size: ${TYPOGRAPHY.button.size} !important;
                                            line-height: ${TYPOGRAPHY.button.lineHeight};
                                        }
                                    }
                                `
                            }}
                            isOpen={isResetPasswordModalOpen}
                            handleClose={() => onResetPasswordClose()}
                            data-test-selector="resetPasswordModal"
                        >
                            <SignInResetPasswordForm onClose={() => onResetPasswordClose()} />
                        </Modal>
                    </GridItem>
                    <GridItem {...styles.signInButtonGridItem}>
                        <Button
                            {...styles.signInButton}
                            css={signInButtonCss}
                            type="submit"
                            data-test-selector="signIn_submit"
                        >
                            {translate("Sign In")}
                        </Button>
                        <Modal
                            headline={translate("Change Password")}
                            {...styles.changePasswordModal}
                            isOpen={isChangePasswordModalOpen}
                            handleClose={onChangePasswordClose}
                        >
                            <SignInChangePasswordForm
                                enteredUserName={userName}
                                enteredPassword={password}
                                instructionsText={fields.changePasswordInstructions}
                                onPasswordChanged={onPasswordChanged}
                            />
                        </Modal>
                    </GridItem>
                </GridContainer>
            </StyledForm>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(SignInExistingAccount)),
    definition: {
        allowedContexts: ["SignInPage", "SignInOverlay"],
        group: "Sign In",
        icon: "LogIn",
        fieldDefinitions: [
            {
                fieldType: "Translatable",
                name: fields.changePasswordInstructions,
                editorTemplate: "RichTextField",
                displayName: "Change Password Instructions",
                defaultValue: "A password change is required for your account before you may continue.",
                extendedConfig: { height: 170 },
                expandedToolbarButtons: {
                    moreMisc: {},
                    code: {},
                },
                sortOrder: 0,
            },
            {
                name: fields.fullWidthButton,
                displayName: "Full Width Button",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
    },
};

export default widgetModule;